/**
 * QR-папки (BQD): карточка со сверкой «план → заказ → оплата», список папок,
 * свои комментарии. Спека: `docs/specs/2026-09-26-bqd-card-design.md`.
 */

import { apiFetch } from "@/lib/api";
import type { SmartIds } from "@/lib/crmSmartButtons";
import type { CargoTrack } from "@/lib/orders/cargo";
import { num } from "@/lib/format";
import type { PlanComment } from "@/components/plans/types";
import { type CargoRef, day } from "@/lib/orders/common";

/** Строка сверки папки — карточка CRM (размер × цвет). `diff` = заказано − план. */
export type BqdRow = {
  card_id: number;
  product_id: number | null;
  barcode: string | null;
  /** Размер карточки площадки (3XL, 4XL…); безразмерная («0») — `null`. */
  size: string | null;
  model_id: number | null;
  /** Склейка моделей (`modelSimilarGroupID`): строки одной склейки идут рядом. */
  group_id: number | null;
  model: string | null;
  model_code: string | null;
  /** Артикулы: наш WB и фабричный — называют строку, когда у модели нет имени. */
  article: string | null;
  factory_code: string | null;
  photo: string | null;
  plan: number;
  ordered: number;
  diff: number;
  units: number;
  accepted: number;
  /** Недопринято штук: Σ max(заказано − принято, 0) по заказам строки; сверхприёмка сюда не входит. */
  unaccepted: number;
  /** Принято сверх заказа: max(принято − заказано, 0). */
  over_accepted: number;
  /** Цена штуки по плану (`slp.supplyProductPriceID`) и в заказе (`spp.supplyProductPriceID`). */
  plan_price: number | null;
  order_price: number | null;
  plan_amount: number;
  order_amount: number;
  price_diff: boolean;
  /** В каких заказах папки лежит карточка и сколько штук в каждом. */
  orders: { order_id: number; qty: number; /** принято по этому заказу */ accepted: number; amount: number }[];
  /** Номера CRM для смарт-кнопок: строки плана, их партии, позиции заказов. */
  plan_ids: number[];
  plan_date_ids: number[];
  position_ids: number[];
  /** Штук в отменённых строках плана (статус 4, 5): в `plan` их нет — не ждём. */
  cancelled: number;
  /** Все строки плана размера, с отменёнными; комментарии — свежие сверху. */
  lines: BqdPlanLine[];
  /**
   * Размер, который уходит поставщику (лист «Размер» выгрузки плана), и
   * свойство фабрики (`supplyArticlePropertyID`): по одному на свойство строк
   * плана размера. `differs` — размер поставщика не наш. Пусто — плана нет.
   */
  supplier: BqdSupplierSize[];
};

export type BqdSupplierSize = {
  property_id: number;
  /** Размер поставщика; `null` — безразмерная. */
  size: string | null;
  /** Свойство фабрики: CN, иначе EN; `null` — не заполнено. */
  property: string | null;
  differs: boolean;
};

/** Строка плана размера в карточке папки. */
export type BqdPlanLine = {
  line_id: number;
  plan_id: number;
  qty: number;
  ordered: number;
  status_id: number;
  status_name: string | null;
  /** Статус партии-даты строки (1 «Активный»): иной — состав не правим. */
  batch_status_id: number;
  comments: PlanComment[];
};

/** Самый свежий комментарий supply среди строк размера; нет — `null`. */
export function lastLineComment(r: Pick<BqdRow, "lines">): PlanComment | null {
  let best: PlanComment | null = null;
  for (const l of r.lines) {
    for (const c of l.comments) {
      if (!best || (c.at ?? "") > (best.at ?? "")) best = c;
    }
  }
  return best;
}

/**
 * Отбор строк сверки папки (спека markup-1058, п. 9): `ordered` — есть в
 * заказах (в т.ч. сверх плана без строки плана); `unordered` — план есть,
 * заказов нет; `diff` — заказано ≠ плану; `under`/`over` — только недозаказ /
 * только сверх плана; `unaccepted` — недопринято > 0.
 */
export type BqdView = "all" | "ordered" | "unordered" | "diff" | "under" | "over" | "unaccepted";

const BQD_VIEWS: readonly BqdView[] = ["all", "ordered", "unordered", "diff", "under", "over", "unaccepted"];

/** Отбор из адреса: неизвестное и пустое — «все». */
export function parseBqdView(s: string | null): BqdView {
  return BQD_VIEWS.find((v) => v === s) ?? "all";
}

export function bqdVisible(rows: BqdRow[], view: BqdView): BqdRow[] {
  switch (view) {
    case "ordered":
      return rows.filter((r) => r.ordered > 0);
    case "unordered":
      return rows.filter((r) => r.plan > 0 && r.ordered === 0);
    case "diff":
      return rows.filter((r) => r.diff !== 0);
    case "under":
      return rows.filter((r) => r.diff < 0);
    case "over":
      return rows.filter((r) => r.diff > 0);
    case "unaccepted":
      return rows.filter((r) => r.unaccepted > 0);
    default:
      return rows;
  }
}

/** Итог строк сверки — первая строка таблицы, по тому, что сейчас видно. */
export function bqdSum(rows: BqdRow[]) {
  const s = { plan: 0, ordered: 0, diff: 0, accepted: 0, plan_amount: 0, order_amount: 0 };
  for (const r of rows) {
    s.plan += r.plan;
    s.ordered += r.ordered;
    s.diff += r.diff;
    s.accepted += r.accepted;
    s.plan_amount += r.plan_amount;
    s.order_amount += r.order_amount;
  }
  return s;
}

/**
 * Номера смарт-кнопок CRM по отмеченным строкам сверки (разметка 28.09.26
 * bqd-720 п.3): товар, модель, склейка, партия плана, строка плана, заказ и
 * позиция в заказе — без повторов, в порядке строк, как `joinUniq` CRM.
 */
export function bqdSmartIds(rows: BqdRow[]): SmartIds {
  const out = {
    mpProductID: [] as number[],
    modelID: [] as number[],
    modelSimilarGroupID: [] as number[],
    mpStockSupplyPlanDateID: [] as number[],
    mpStockSupplyPlanID: [] as number[],
    supplyOrderID: [] as number[],
    supplyOrderProductPositionID: [] as number[],
  };
  const положить = (список: number[], id: number | null) => {
    if (id && !список.includes(id)) список.push(id);
  };
  for (const r of rows) {
    положить(out.mpProductID, r.product_id);
    положить(out.modelID, r.model_id);
    положить(out.modelSimilarGroupID, r.group_id);
    r.plan_date_ids.forEach((id) => положить(out.mpStockSupplyPlanDateID, id));
    r.plan_ids.forEach((id) => положить(out.mpStockSupplyPlanID, id));
    r.orders.forEach((o) => положить(out.supplyOrderID, o.order_id));
    r.position_ids.forEach((id) => положить(out.supplyOrderProductPositionID, id));
  }
  return out;
}

/**
 * Состояние приёмки строки: `done` — всё заказанное принято; `missing` —
 * не принято, а все заказы строки уже приехали в РФ (пропажа); `waiting` —
 * не принято, часть заказов ещё в пути (не ошибка); `none` — не заказано.
 * `k` — не принято, `arrived/total` — приехавшие заказы строки с qty > 0.
 */
export function acceptState(
  r: BqdRow,
  заказы: Map<number, BqdOrder>,
): { kind: "done" | "waiting" | "missing" | "none"; k: number; arrived: number; total: number } {
  const k = r.unaccepted;
  const свои = r.orders.filter((o) => o.qty > 0);
  const total = свои.length;
  const arrived = свои.filter((o) => заказы.get(o.order_id)?.eta_kind === "arrived").length;
  if (r.ordered <= 0) return { kind: "none", k, arrived, total };
  if (k <= 0) return { kind: "done", k, arrived, total };
  return { kind: total > 0 && arrived === total ? "missing" : "waiting", k, arrived, total };
}

/** Метка чата приёмки; `link` пуст у простой группы (ссылки на сообщение нет). */
export type BqdChatMark = {
  order_id: number;
  city_id: number | null;
  city: string | null;
  at: string | null;
  link: string | null;
  user: string | null;
  /** Ответы (`reply`) по заказу, не по цепочке. */
  replies: number;
};

/** Ступень «Пути папки»: состояние считает сервер (`bqd/path.py`), экран рисует. */
export type BqdStep = {
  key: "plan" | "orders" | "payment" | "cargo" | "russia" | "accept";
  title: string;
  state: "done" | "partial" | "ahead" | "attention";
  current: boolean;
  value: string;
  note: string;
  /** Покрытие живых заказов; `null` — ступень не про заказы. */
  coverage: { done: number; total: number } | null;
  /** Дата первого этапа ступени; `null` — этапов нет. */
  date: string | null;
  /** Сумма словом («17 000 ¥»): у плана и заказов; `null` — суммы нет. */
  sum: string | null;
  /** Этапы ступени по времени: платёж на каждую дату, накладная с номером, трек. */
  items: BqdEvent[];
};

/**
 * «Что сейчас»: заголовок, следующий шаг и сверки; `view` — отбор товаров, куда
 * вести; `card_id` — карточка CRM строки сверки (ключ строки), у денег и «и ещё N» — `null`.
 */
export type BqdNow = {
  title: string;
  next: string | null;
  attention: { text: string; view: "unaccepted" | null; card_id: number | null }[];
  /** Показывать плашку: живые заказы (кроме «Всё принято» без сверок) или есть сигнал. */
  show: boolean;
};

/** Что открывает ссылка события: платёж, накладную, трек или заказ. */
export type BqdRef = { type: "payment" | "cargo" | "track" | "order"; id: number; label: string };

/** Событие истории папки; `at` — дата (`2026-09-13`) или дата со временем. */
export type BqdEvent = {
  at: string;
  kind: string;
  label: string;
  /** Заказы, к которым относится событие; пусто — к папке целиком. */
  order_ids: number[];
  source: "user" | "robot" | "crm";
  user: string | null;
  ref: BqdRef | null;
};

/** Карточка QR-папки (BQD): план, заказы и оплаты со сверкой по товару. */
export type BqdCard = {
  head: {
    qr_id: number;
    created_at: string | null;
    plan_date: string | null;
    status: string | null;
    supplier_id: number | null;
    supplier: string;
  };
  orders: {
    order_id: number;
    platform_order: string | null;
    ordered_at: string | null;
    status: string | null;
    cargo_stage: string | null;
    sum_goods: number;
    sum_delivery: number;
    /** Физических штук в заказе. */
    units: number;
    /** Приход в РФ тем же расчётом, что «в заказах» карточки товара. */
    eta_kind: "arrived" | "eta" | "none";
    eta: string | null;
    /** Принято физических штук заказа. */
    accepted: number;
    /** `dateCargoDone`: когда заказ пришёл в РФ; `null` — нет. */
    arrived_at: string | null;
    status_id: number | null;
    cargo_status_id: number | null;
    /** Заказ уже в РФ (по дате прихода или статусу карго 4/5/6). */
    in_rf: boolean;
    /** Карго-накладные заказа; `units`/`orders` — по ВСЕЙ накладной. */
    cargo: (CargoRef & { eta: string | null; units: number; orders: number })[];
    /** Треки по России заказа — та же строка, что в «Заказах» и «Карго-накладных». */
    ru: CargoTrack[];
    /** Номера CRM для смарт-кнопок заказа: папки, партии плана, строки плана. */
    qr_ids: number[];
    plan_date_ids: number[];
    plan_ids: number[];
  }[];
  /** Метки чата приёмки: последнее наше сообщение на пару заказ × город. */
  chat: BqdChatMark[];
  /** Путь папки: шесть ступеней, «что сейчас», приёмка по живым заказам. */
  path: { steps: BqdStep[]; now: BqdNow; accept: { ordered: number; accepted: number; left: number } };
  /** История событий папки по времени; `missing` — чего CRM не передала. */
  history: { events: BqdEvent[]; missing: string[] };
  payments: {
    payment_id: number;
    amount: number;
    rate: number | null;
    at: string | null;
    via_bqd: boolean;
    via_orders: number[];
    /** Папки платежа в CRM, если этой среди них нет: попал сюда только через заказ. */
    other_folders: number[];
  }[];
  rows: BqdRow[];
  /** Комментарии areserv к партиям папки по номеру партии (ключ JSON — строка). */
  plan_comments: Record<string, PlanComment[]>;
  comments: BqdComment[];
  totals: {
    plan: number;
    ordered: number;
    units: number;
    accepted: number;
    under: number;
    over: number;
    /** План по цене плана, заказано по цене позиции. */
    plan_amount: number;
    ordered_amount: number;
    price_diff: number;
    paid: number;
    /** Оплачено − сумма плана: минус — осталось доплатить до плана. */
    balance: number;
  };
};

/**
 * События истории одного заказа: его собственные и общие для папки
 * (`order_ids` пуст) — в порядке сервера.
 */
export function eventsOfOrder(events: BqdEvent[], orderId: number): BqdEvent[] {
  return events.filter((e) => e.order_ids.length === 0 || e.order_ids.includes(orderId));
}

/** Момент события: `13.09.26` для даты, `12.09.26 09:30` для даты со временем. */
export function eventAt(at: string): string {
  const [дата, время] = at.split(/[T ]/);
  const д = day(дата);
  const м = /^\d{2}:\d{2}/.exec(время ?? "");
  return м ? `${д} ${м[0]}` : д;
}

const ИСТОЧНИК = { user: "человек", robot: "робот", crm: "CRM" } as const;

/** Подпись источника события справа в строке истории. */
export function sourceLabel(s: BqdEvent["source"]): string {
  return ИСТОЧНИК[s];
}

/** Заказ в карточке папки. */
export type BqdOrder = BqdCard["orders"][number];

export function bqdHref(qrId: number): string {
  return `/bqd/${qrId}`;
}

/**
 * Номер папки из того, что вбили на экране «BQD»: `720`, `bqd-720`, `BQD 720`.
 * Не номер — `null`, переход не делается.
 */
export function parseBqdId(text: string): number | null {
  const m = /^\s*(?:bqd[\s-]*)?(\d{1,9})\s*$/i.exec(text);
  if (!m) return null;
  const id = Number(m[1]);
  return id > 0 ? id : null;
}

export function loadBqdCard(qrId: number): Promise<BqdCard> {
  return apiFetch<BqdCard>(`/api/bqd/${qrId}`);
}

/** Статус CRM «Отменён» у заказа фабрике. */
const ОТМЕНЁН = 7;

/**
 * Ячейка «осталось принять» полосы итогов. Остаток — по живым заказам
 * (`path.accept`), а «принято» рядом — по всем: при отменённых заказах под
 * числом подпись «без отменённых», иначе арифметика полосы не сходится. Живых
 * заказов нет — прочерк, не «0 шт» (прочерк ≠ ноль).
 */
export function bqdLeft(карточка: Pick<BqdCard, "orders" | "path">): { text: string; caption: string | null } {
  const живых = карточка.orders.filter((o) => o.status_id !== ОТМЕНЁН).length;
  const отменённых = карточка.orders.length - живых;
  return {
    text: живых ? `${num(карточка.path.accept.left)} шт` : "—",
    caption: отменённых ? "без отменённых" : null,
  };
}

/** Раскрытые истории заказов из `?history=` (номера через запятую). */
export function parseBqdHistory(s: string | null): Set<number> {
  return new Set(
    (s ?? "")
      .split(",")
      .map(Number)
      .filter((id) => Number.isInteger(id) && id > 0),
  );
}

/** Параметры адреса с раскрытой (или закрытой) историей заказа; чужие параметры не трогает. */
export function withBqdHistory(params: URLSearchParams, orderId: number, открыта: boolean): URLSearchParams {
  const next = new URLSearchParams(params);
  const истории = parseBqdHistory(next.get("history"));
  if (открыта) истории.add(orderId);
  else истории.delete(orderId);
  if (истории.size) next.set("history", [...истории].join(","));
  else next.delete("history");
  return next;
}

/**
 * Применять ответ только ПОСЛЕДНЕГО запроса: два перечитывания подряд могут
 * вернуться в обратном порядке, и старый ответ затёр бы свежий. Ошибку
 * перечитывания глотаем — на экране остаётся прежняя карточка.
 */
export function latestOnly<T>(): (запрос: Promise<T>, применить: (v: T) => void) => void {
  let последний = 0;
  return (запрос, применить) => {
    const мой = ++последний;
    запрос.then(
      (v) => {
        if (мой === последний) применить(v);
      },
      () => undefined,
    );
  };
}

/** Строка списка QR-папок: итоги карточки папки, накладные и сигнал. */
export type BqdFolder = {
  qr_id: number;
  created_at: string | null;
  status: string | null;
  /** Статус папки CRM (1 Активный … 4 Отправлен); по нему сервер решает, база — план или заказы. */
  status_id: number | null;
  supplier_id: number | null;
  supplier: string;
  orders: number;
  plan: number;
  ordered: number;
  /** Остаток плана — недозаказ, как на карточке папки. */
  under: number;
  units: number;
  accepted: number;
  /** Штуки в заказах, ещё не принятые. */
  open: number;
  plan_amount: number;
  /** Сумма позиций заказов папки по цене позиции; заказов нет — 0. */
  order_amount: number;
  paid: number;
  /** Оплачено − сумма плана. */
  balance: number;
  /**
   * База суммы (сервер, `bqd/basis.py`): `order` — заказ больше плана или папка
   * отправлена; иначе `plan`. `base_amount` — сумма базы, `balance_base` —
   * оплачено − база, `balance_order` — оплачено − заказы.
   */
  amount_basis: "plan" | "order";
  base_amount: number;
  balance_base: number;
  balance_order: number;
  /** Суммы плана и заказов разошлись больше допуска (1 ¥); `amount_gap` = заказы − план. */
  amount_diverged: boolean;
  amount_gap: number;
  /** Папка закрыта по статусу («Отправлен»): живёт по заказам. */
  amount_closed: boolean;
  /** «Заказ не создан»: в плане есть, заказов нет. */
  order_missing: boolean;
  /** «План и заказ расходятся»: загрузили частично, сверх плана или цена. */
  plan_order_diff: boolean;
  /** Самый свежий оплаченный платёж папки; нет оплат — `null`. */
  last_paid_at: string | null;
  cargo: (CargoRef & { eta: string | null })[];
  eta_kind: "arrived" | "eta" | "none";
  eta: string | null;
  /** Оплачено не меньше суммы плана, а карго-накладной нет ни у одного заказа. */
  paid_no_cargo: boolean;
  /** Свои комментарии к папке, свежие сверху. */
  comments: BqdComment[];
};

export function loadBqdList(): Promise<{ rows: BqdFolder[] }> {
  return apiFetch<{ rows: BqdFolder[] }>("/api/bqd");
}

/** Комментарий к QR-папке — своя таблица `bqd_comment`, в CRM не пишем. */
export type BqdComment = {
  id: number;
  /** Время по Москве, ISO. */
  at: string | null;
  text: string;
  user_id: number;
  user: string | null;
};

export function addBqdComment(qrId: number, text: string) {
  return apiFetch<{ qr_id: number; comment: BqdComment }>("/api/bqd/comment", {
    method: "POST",
    body: JSON.stringify({ qr_id: qrId, text }),
  });
}

export type BqdListView = "alarm" | "work" | "all";

/** Трёхпозиционный отбор: `yes` — есть, `no` — нет, пусто — все. */
export type BqdTri = "" | "yes" | "no";

/** Быстрый отбор по заказу (разметка 04.11.26 #8): без заказа / план и заказ расходятся. */
export type BqdOrderFilter = "" | "none" | "diff";

/** Отбор списка, кроме вида и поиска: фабрики, заказ, накладная, оплата. */
export type BqdListFilters = {
  /** Номера фабрик из `?supplier=1,2`; пусто — все. */
  suppliers: number[];
  order: BqdOrderFilter;
  invoice: BqdTri;
  pay: BqdTri;
};

export const BQD_NO_FILTERS: BqdListFilters = { suppliers: [], order: "", invoice: "", pay: "" };

/**
 * Отбор списка папок (разметка 28.09.26 11:47 п.3): «оплачено, накладной нет»
 * — сигнал владельца; «в работе» — есть не принятое или остаток плана. Поиск —
 * по номеру папки и поставщику. Остальное (разметка 04.11.26 #6, #8, #10) —
 * `filters`: фабрика, заказ не создан / расходится, накладная и оплата есть / нет.
 */
export function bqdListVisible(
  rows: BqdFolder[],
  view: BqdListView,
  q: string,
  filters: BqdListFilters = BQD_NO_FILTERS,
): BqdFolder[] {
  const s = q.trim().toLowerCase().replace(/^bqd[\s-]*/, "");
  const { suppliers, order, invoice, pay } = filters;
  return rows.filter(
    (r) =>
      (view === "all" || (view === "alarm" ? r.paid_no_cargo : r.open > 0 || r.under > 0)) &&
      (!s || String(r.qr_id).startsWith(s) || r.supplier.toLowerCase().includes(s)) &&
      (!suppliers.length || (r.supplier_id !== null && suppliers.includes(r.supplier_id))) &&
      (order === "" || (order === "none" ? r.order_missing : r.plan_order_diff)) &&
      (invoice === "" || (invoice === "yes") === r.cargo.length > 0) &&
      (pay === "" || (pay === "yes") === r.paid > 0),
  );
}

/** `folder` — по дате создания папки (она же номер); `paid` — по дате последней оплаты. */
export type BqdListSort = "folder" | "paid";

function tri(v: string | null): BqdTri {
  return v === "yes" || v === "no" ? v : "";
}

/** Фабрики из адреса: `supplier=1,2` — номера; мусор отбрасывается. */
export function parseBqdSuppliers(raw: string | null): number[] {
  const ids = (raw ?? "")
    .split(",")
    .filter((id) => /^\d+$/.test(id))
    .map(Number)
    .filter((id) => id > 0);
  return [...new Set(ids)];
}

/**
 * Отбор, поиск и порядок списка папок — из адреса, чтобы ссылкой передать
 * другому (разметка 28.09.26 13:26, 04.11.26 #6, #8, #10). Порядок по
 * умолчанию — по дате создания папки, новые сверху, в любом виде: «последняя
 * оплата» — переключатель (`sort=paid`), а не дефолт.
 */
export function bqdListParams(p: URLSearchParams): {
  view: BqdListView;
  q: string;
  sort: BqdListSort;
  desc: boolean;
  filters: BqdListFilters;
} {
  const v = p.get("view");
  const o = p.get("ord");
  return {
    view: v === "alarm" || v === "all" ? v : "work",
    q: p.get("q") ?? "",
    sort: p.get("sort") === "paid" ? "paid" : "folder",
    desc: p.get("dir") !== "asc",
    filters: {
      suppliers: parseBqdSuppliers(p.get("supplier")),
      order: o === "none" || o === "diff" ? o : "",
      invoice: tri(p.get("inv")),
      pay: tri(p.get("pay")),
    },
  };
}

/**
 * Порядок списка папок. `folder` — по дате создания (при равенстве или
 * пустой дате — по номеру). Папки без оплаты при `paid` — всегда внизу.
 */
export function bqdListSorted(rows: BqdFolder[], sort: BqdListSort, desc: boolean): BqdFolder[] {
  const знак = desc ? -1 : 1;
  return [...rows].sort((a, b) => {
    if (sort === "paid") {
      if (!a.last_paid_at !== !b.last_paid_at) return a.last_paid_at ? -1 : 1;
      if (a.last_paid_at && b.last_paid_at && a.last_paid_at !== b.last_paid_at) {
        return (a.last_paid_at < b.last_paid_at ? -1 : 1) * знак;
      }
      return b.qr_id - a.qr_id;
    }
    const ca = a.created_at ?? "";
    const cb = b.created_at ?? "";
    if (ca !== cb) return (ca < cb ? -1 : 1) * знак;
    return (a.qr_id - b.qr_id) * знак;
  });
}

/** Строки сверки группой по модели: без модели — сама карточка. */
export type BqdGroup = { key: string; строки: BqdRow[] };

export function groupBqdRows(строки: BqdRow[]): BqdGroup[] {
  const группы = new Map<string, BqdGroup>();
  for (const r of строки) {
    const key = r.model_id !== null ? `m${r.model_id}` : `c${r.card_id}`;
    const г = группы.get(key);
    if (г) г.строки.push(r);
    else группы.set(key, { key, строки: [r] });
  }
  return [...группы.values()];
}
